import { createSelector } from "reselect";
import { RootState } from "../store";
import { api } from "../api/api";

export const selectUserSyncData = (state: RootState) =>
  api.endpoints.usersSync.select()(state).data;

export const selectUserOrg = createSelector(
  [selectUserSyncData],
  (sync) => sync?.org
);

export const selectUserOrgId = createSelector(
  [selectUserOrg],
  (org) => org?.id
);

export const selectUserOrgEmail = createSelector(
  [selectUserOrg],
  (org) => org?.email || ""
);

export const selectKYC = createSelector(
  [selectUserSyncData],
  (response) => response?.kyc
);

export const selectUserCurrentSubscription = createSelector(
  [selectUserSyncData],
  (response) => response?.current_subscription
);

export const selectSubscription = createSelector(
  [selectUserCurrentSubscription],
  (current_subscription) => {
    return current_subscription?.subscription;
  }
);

export enum ORG_STATUS {
  TRIAL_ACTIVE = "TRIAL_ACTIVE",
  PAID_ACTIVE = "PAID_ACTIVE",
  TRIAL_EXPIRED = "TRIAL_EXPIRED",
  PAID_EXPIRED = "PAID_EXPIRED",
  ALL = "ALL",
  PAID_ABOUT_TO_EXPIRE = "PAID_ABOUT_TO_EXPIRE",
  REFUNDED = "REFUNDED",
  DEMO_MODE = "DEMO_MODE",
  PAID_ABOUT_TO_EXPIRE_VERY_SOON = "PAID_ABOUT_TO_EXPIRE_VERY_SOON",
}

export const selectOrgStatus = createSelector(
  [selectUserCurrentSubscription],
  (current_subscription) => {
    const {
      is_active,
      is_expiring_soon,
      is_paid_org,
      is_refunded,
      is_trial,
      is_trial_active,
    } = current_subscription || {};
    //paid active
    if (is_paid_org && is_expiring_soon) {
      return ORG_STATUS.PAID_ABOUT_TO_EXPIRE;
    }

    if (is_paid_org && is_active) {
      return ORG_STATUS.PAID_ACTIVE;
    }

    //paid inactive
    if (is_paid_org && !is_active) {
      return ORG_STATUS.PAID_EXPIRED;
    }

    //trial
    if (is_trial && is_trial_active) {
      return ORG_STATUS.TRIAL_ACTIVE;
    }
    if (is_trial && !is_trial_active) {
      return ORG_STATUS.TRIAL_EXPIRED;
    }

    //refunded
    if (is_refunded) {
      return ORG_STATUS.REFUNDED;
    }

    //demo
    if (!is_active && !is_paid_org && !is_trial && !is_trial_active) {
      return ORG_STATUS.DEMO_MODE;
    }
    return ORG_STATUS.ALL;
  }
);

export enum SUBSCRIPTION_PLAN_TYPE {
  Trial = "Trial",
  Standard = "Standard",
  Premium = "Premium",
  Custom = "Custom",
}

export const selectSubscriptionPlanType = createSelector(
  [selectSubscription],
  (subscription) => {
    const title = (subscription?.title || "").toLowerCase();
    if (title.includes("standard")) {
      return SUBSCRIPTION_PLAN_TYPE.Standard;
    }
    if (title.includes("premium")) {
      return SUBSCRIPTION_PLAN_TYPE.Premium;
    }
    if (title.includes("custom")) {
      return SUBSCRIPTION_PLAN_TYPE.Custom;
    }
    return SUBSCRIPTION_PLAN_TYPE.Trial;
  }
);

export const selectKYCStatus = createSelector(
  [selectKYC],
  (kyc) => kyc?.status || ""
);

export const selectOrgCreationDate = createSelector(
  [selectUserOrg],
  (org) => org?.created_at || ""
);

export const selectIsDemoUser = createSelector(
  [selectUserCurrentSubscription],
  (subscription) => {
    return (
      subscription?.is_active === false &&
      subscription?.is_trial === false &&
      subscription?.is_trial_active === false
    );
  }
);

export const selectIsTrialUser = createSelector(
  [selectUserCurrentSubscription],
  (subscription) => {
    return subscription?.is_trial === true && subscription?.is_active === false;
  }
);

export const selectIsUnpaidUser = createSelector(
  [selectIsDemoUser, selectIsTrialUser],
  (demo, trial) => {
    return demo || trial;
  }
);

export const selectTalktime = createSelector(
  [selectUserSyncData],
  (usersSync) => usersSync?.talktime
);

export const UnAuthoisedMessage =
  "Only Business owners can access Web Dashboard.";
